import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const RECIPIENT_GROUPS = [
    {
        value: 'all_users',
        label: 'Registered Users',
        description: 'All verified user accounts',
    },
    {
        value: 'newsletter',
        label: 'Newsletter Subscribers',
        description: 'Active, confirmed newsletter subscribers only',
    },
    {
        value: 'all',
        label: 'Everyone',
        description: 'Registered users + newsletter subscribers (deduplicated)',
    },
    {
        value: 'manual_single',
        label: 'Single Email Address',
        description: 'Manually type one recipient email address',
    },
];

const SENDER_OPTIONS = [
    { value: 'noreply@mybricklog.com', label: 'MyBrickLog <noreply@mybricklog.com>' },
    { value: 'legal@mybricklog.com', label: 'MyBrickLog Legal <legal@mybricklog.com>' },
    { value: 'jason@mybricklog.com', label: 'Jason <jason@mybricklog.com>' },
];

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ── Small sub-components ──────────────────────────────────────────────────────

const RecipientBadge = ({ count, loading }) => {
    if (loading) {
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-gray-100 text-gray-500 text-sm">
                <span className="animate-spin h-3.5 w-3.5 border-b-2 border-gray-400 rounded-full inline-block" />
                Loading…
            </span>
        );
    }
    return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-red-50 text-red-700 text-sm font-medium border border-red-200">
            {count.toLocaleString()} recipient{count !== 1 ? 's' : ''}
        </span>
    );
};

const ConfirmModal = ({ subject, recipientGroup, recipientCount, senderEmail, useBrandedTemplate, onConfirm, onCancel, isSending }) => {
    const groupLabel = RECIPIENT_GROUPS.find(g => g.value === recipientGroup)?.label ?? recipientGroup;
    const senderLabel = SENDER_OPTIONS.find(s => s.value === senderEmail)?.label ?? senderEmail;
    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
                <div className="p-6 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">Confirm Send</h3>
                </div>
                <div className="p-6 space-y-4">
                    <p className="text-sm text-gray-600">
                        You are about to send an email to{' '}
                        <span className="font-semibold text-gray-900">
                            {recipientCount.toLocaleString()} recipient{recipientCount !== 1 ? 's' : ''}
                        </span>
                        . This action cannot be undone.
                    </p>
                    <div className="bg-gray-50 rounded-md p-4 text-sm space-y-2 border border-gray-200">
                        <div className="flex gap-2">
                            <span className="text-gray-500 w-20 flex-shrink-0">Subject:</span>
                            <span className="font-medium text-gray-900 break-all">{subject}</span>
                        </div>
                        <div className="flex gap-2">
                            <span className="text-gray-500 w-20 flex-shrink-0">To:</span>
                            <span className="font-medium text-gray-900">{groupLabel}</span>
                        </div>
                        <div className="flex gap-2">
                            <span className="text-gray-500 w-20 flex-shrink-0">Count:</span>
                            <span className="font-medium text-gray-900">{recipientCount.toLocaleString()} emails</span>
                        </div>
                        <div className="flex gap-2">
                            <span className="text-gray-500 w-20 flex-shrink-0">From:</span>
                            <span className="font-medium text-gray-900">{senderLabel}</span>
                        </div>
                        <div className="flex gap-2">
                            <span className="text-gray-500 w-20 flex-shrink-0">Format:</span>
                            <span className="font-medium text-gray-900">
                                {useBrandedTemplate ? 'MyBrickLog branded template' : 'Plaintext / no template'}
                            </span>
                        </div>
                    </div>
                </div>
                <div className="p-6 pt-0 flex gap-3 justify-end">
                    <button
                        onClick={onCancel}
                        disabled={isSending}
                        className="px-4 py-2 text-sm font-medium rounded-md text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 disabled:opacity-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isSending}
                        className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 transition-colors"
                    >
                        {isSending ? (
                            <>
                                <span className="animate-spin h-4 w-4 border-b-2 border-white rounded-full inline-block" />
                                Sending…
                            </>
                        ) : (
                            <>
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                                </svg>
                                Send Now
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

// ── Main component ────────────────────────────────────────────────────────────

const AdminEmail = () => {
    const { user, isLoading: authLoading, checkAdminStatus } = useAuth();
    const navigate = useNavigate();
    useDocumentTitle('Admin Email');

    const [isLoading, setIsLoading]       = useState(true);
    const [isAuthorized, setIsAuthorized] = useState(false);

    // Form state
    const [recipientGroup, setRecipientGroup] = useState('all_users');
    const [manualRecipient, setManualRecipient] = useState('');
    const [senderEmail, setSenderEmail]         = useState('noreply@mybricklog.com');
    const [useBrandedTemplate, setUseBrandedTemplate] = useState(true);
    const [subject, setSubject]               = useState('');
    const [bodyHtml, setBodyHtml]             = useState('');
    const [showPreview, setShowPreview]       = useState(false);

    // Recipient preview state
    const [recipients, setRecipients]           = useState([]);
    const [recipientCount, setRecipientCount]   = useState(0);
    const [loadingRecipients, setLoadingRecipients] = useState(false);
    const [showRecipientList, setShowRecipientList] = useState(false);

    // Send state
    const [showConfirm, setShowConfirm] = useState(false);
    const [isSending, setIsSending]     = useState(false);
    const [sendResult, setSendResult]   = useState(null); // { sent_count, failed_count, total, failed[] }
    const [sendError, setSendError]     = useState(null);

    const normalizedManualRecipient = manualRecipient.trim().toLowerCase();
    const isManualRecipientValid = EMAIL_REGEX.test(normalizedManualRecipient);

    // ── Auth guard ────────────────────────────────────────────────────────────
    useEffect(() => {
        const verify = async () => {
            if (authLoading) return;
            if (!user) { navigate('/'); return; }
            const isAdmin = await checkAdminStatus();
            if (!isAdmin) { navigate('/'); return; }
            setIsAuthorized(true);
            setIsLoading(false);
        };
        verify();
    }, [user, authLoading, checkAdminStatus, navigate]);

    // ── Load recipient preview on group change ────────────────────────────────
    const loadRecipients = useCallback(async (group) => {
        if (group === 'manual_single') {
            if (isManualRecipientValid) {
                setRecipients([{ email: normalizedManualRecipient, name: '', source: 'manual' }]);
                setRecipientCount(1);
            } else {
                setRecipients([]);
                setRecipientCount(0);
            }
            setLoadingRecipients(false);
            return;
        }

        setLoadingRecipients(true);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/admin_get_email_recipients.php?group=${group}`,
                { credentials: 'include' }
            );
            const data = await res.json();
            if (data.success) {
                setRecipients(data.recipients);
                setRecipientCount(data.count);
            }
        } catch (err) {
            console.error('Failed to load recipients:', err);
        } finally {
            setLoadingRecipients(false);
        }
    }, [isManualRecipientValid, normalizedManualRecipient]);

    useEffect(() => {
        if (!isAuthorized) return;
        loadRecipients(recipientGroup);
    }, [isAuthorized, recipientGroup, loadRecipients]);

    // ── Send ──────────────────────────────────────────────────────────────────
    const handleSend = async () => {
        setIsSending(true);
        setSendError(null);
        try {
            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/admin_send_bulk_email.php`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({
                        subject,
                        body_html: bodyHtml,
                        recipient_group: recipientGroup,
                        recipient_email: recipientGroup === 'manual_single' ? normalizedManualRecipient : null,
                        sender_email: senderEmail,
                        use_branded_template: useBrandedTemplate,
                    }),
                }
            );
            const data = await res.json();
            if (data.success) {
                setSendResult(data);
                setShowConfirm(false);
                // Reset form on success
                setSubject('');
                setBodyHtml('');
                setShowPreview(false);
                setManualRecipient('');
            } else {
                setSendError(data.error || 'Send failed. Please try again.');
                setShowConfirm(false);
            }
        } catch (err) {
            setSendError('Network error. Please try again.');
            setShowConfirm(false);
        } finally {
            setIsSending(false);
        }
    };

    const canSend =
        subject.trim().length > 0 &&
        bodyHtml.trim().length > 0 &&
        recipientCount > 0 &&
        (recipientGroup !== 'manual_single' || isManualRecipientValid);

    // ── Loading / auth ────────────────────────────────────────────────────────
    if (isLoading || authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto mb-4" />
                    <p className="text-gray-500">Verifying admin access…</p>
                </div>
            </div>
        );
    }

    if (!isAuthorized) return null;

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">

                {/* Breadcrumb */}
                <nav className="flex mb-6" aria-label="Breadcrumb">
                    <ol className="inline-flex items-center space-x-1 md:space-x-2">
                        <li className="inline-flex items-center">
                            <Link to="/admin" className="inline-flex items-center text-sm font-medium text-gray-600 hover:text-red-600">
                                <svg className="w-4 h-4 mr-1.5" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                                </svg>
                                Admin
                            </Link>
                        </li>
                        <li>
                            <div className="flex items-center">
                                <svg className="w-4 h-4 text-gray-400 mx-1" fill="currentColor" viewBox="0 0 20 20">
                                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                                </svg>
                                <span className="text-sm font-medium text-gray-500">Email Users</span>
                            </div>
                        </li>
                    </ol>
                </nav>

                {/* Header */}
                <div className="mb-6">
                    <h1 className="text-3xl font-bold text-gray-900">Email Users</h1>
                    <p className="mt-1 text-gray-500">
                        Send a message to registered users and/or newsletter subscribers via AWS SES.
                    </p>
                </div>

                {/* Send result banner */}
                {sendResult && (
                    <div className={`mb-6 rounded-lg border p-4 ${
                        sendResult.failed_count === 0
                            ? 'bg-green-50 border-green-200'
                            : 'bg-yellow-50 border-yellow-200'
                    }`}>
                        <div className="flex items-start gap-3">
                            <svg
                                className={`h-5 w-5 mt-0.5 flex-shrink-0 ${
                                    sendResult.failed_count === 0 ? 'text-green-500' : 'text-yellow-500'
                                }`}
                                fill="none" viewBox="0 0 24 24" stroke="currentColor"
                            >
                                {sendResult.failed_count === 0 ? (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                ) : (
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.232 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                )}
                            </svg>
                            <div className="flex-1">
                                <p className={`font-semibold text-sm ${
                                    sendResult.failed_count === 0 ? 'text-green-800' : 'text-yellow-800'
                                }`}>
                                    {sendResult.failed_count === 0
                                        ? `All ${sendResult.sent_count} emails sent successfully.`
                                        : `${sendResult.sent_count} of ${sendResult.total} emails sent. ${sendResult.failed_count} failed.`}
                                </p>
                                {sendResult.failed_count > 0 && (
                                    <ul className="mt-2 text-sm text-yellow-700 space-y-1">
                                        {sendResult.failed.map((f, i) => (
                                            <li key={i}>
                                                <span className="font-mono">{f.email}</span>
                                                {f.error && <span className="text-yellow-600"> - {f.error}</span>}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                            <button
                                onClick={() => setSendResult(null)}
                                className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                            >
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}

                {/* Send error banner */}
                {sendError && (
                    <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                            <svg className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <p className="text-sm text-red-700 font-medium">{sendError}</p>
                            <button onClick={() => setSendError(null)} className="text-gray-400 hover:text-gray-600 ml-auto flex-shrink-0">
                                <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}

                <div className="space-y-6">
                    {/* ── Recipients ─────────────────────────────────────────── */}
                    <div className="bg-white border border-gray-200 shadow-sm rounded-lg">
                        <div className="px-5 py-4 border-b border-gray-100">
                            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Recipients</h2>
                        </div>
                        <div className="px-5 py-4">
                            <div className="space-y-3">
                                {RECIPIENT_GROUPS.map((group) => (
                                    <label
                                        key={group.value}
                                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                                            recipientGroup === group.value
                                                ? 'border-red-300 bg-red-50'
                                                : 'border-gray-200 hover:bg-gray-50'
                                        }`}
                                    >
                                        <input
                                            type="radio"
                                            name="recipient_group"
                                            value={group.value}
                                            checked={recipientGroup === group.value}
                                            onChange={() => {
                                                setRecipientGroup(group.value);
                                                setSendResult(null);
                                            }}
                                            className="mt-0.5 accent-red-600"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap">
                                                <span className="text-sm font-medium text-gray-900">{group.label}</span>
                                                {recipientGroup === group.value && (
                                                    <RecipientBadge count={recipientCount} loading={loadingRecipients} />
                                                )}
                                            </div>
                                            <p className="text-xs text-gray-500 mt-0.5">{group.description}</p>
                                        </div>
                                    </label>
                                ))}
                            </div>

                            {recipientGroup === 'manual_single' && (
                                <div className="mt-4">
                                    <label htmlFor="manual-recipient" className="block text-sm font-medium text-gray-700 mb-1">
                                        Recipient Email <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        id="manual-recipient"
                                        type="email"
                                        value={manualRecipient}
                                        onChange={(e) => {
                                            setManualRecipient(e.target.value);
                                            setSendResult(null);
                                        }}
                                        placeholder="name@example.com"
                                        className="block w-full max-w-md px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                    />
                                    {!isManualRecipientValid && manualRecipient.trim().length > 0 && (
                                        <p className="mt-1 text-xs text-red-600">
                                            Enter a valid email address to send.
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Recipient list toggle */}
                            {recipientCount > 0 && (
                                <div className="mt-4">
                                    <button
                                        type="button"
                                        onClick={() => setShowRecipientList(p => !p)}
                                        className="text-sm text-red-600 hover:text-red-700 font-medium"
                                    >
                                        {showRecipientList
                                            ? 'Hide recipient list'
                                            : `View all ${recipientCount} recipients`}
                                    </button>

                                    {showRecipientList && (
                                        <div className="mt-3 border border-gray-200 rounded-lg overflow-hidden">
                                            <div className="max-h-56 overflow-y-auto">
                                                <table className="min-w-full divide-y divide-gray-100 text-sm">
                                                    <thead className="bg-gray-50 sticky top-0">
                                                        <tr>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                                                            <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-100">
                                                        {recipients.map((r, i) => (
                                                            <tr key={i} className="hover:bg-gray-50">
                                                                <td className="px-4 py-2 font-mono text-xs text-gray-700">{r.email}</td>
                                                                <td className="px-4 py-2 text-gray-700">{r.name || <span className="text-gray-400">-</span>}</td>
                                                                <td className="px-4 py-2 text-gray-500 text-xs">{r.source}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {!loadingRecipients && recipientCount === 0 && (
                                <p className="mt-3 text-sm text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-md px-3 py-2">
                                    No eligible recipients in this group.
                                </p>
                            )}
                        </div>
                    </div>

                    {/* ── Compose ────────────────────────────────────────────── */}
                    <div className="bg-white border border-gray-200 shadow-sm rounded-lg">
                        <div className="px-5 py-4 border-b border-gray-100">
                            <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Compose</h2>
                        </div>
                        <div className="px-5 py-5 space-y-5">
                            {/* From */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">From</label>
                                <select
                                    value={senderEmail}
                                    onChange={(e) => setSenderEmail(e.target.value)}
                                    className="block w-full max-w-md px-3 py-2 text-sm border border-gray-300 rounded-md bg-white focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                >
                                    {SENDER_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Template toggle */}
                            <div className="rounded-lg border border-gray-200 p-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div>
                                        <p className="text-sm font-medium text-gray-900">Email Format</p>
                                        <p className="mt-0.5 text-xs text-gray-500">
                                            Choose whether to wrap the message in the MyBrickLog template.
                                        </p>
                                    </div>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={useBrandedTemplate}
                                        onClick={() => setUseBrandedTemplate((prev) => !prev)}
                                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                                            useBrandedTemplate ? 'bg-red-600' : 'bg-gray-300'
                                        }`}
                                    >
                                        <span
                                            className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                                                useBrandedTemplate ? 'translate-x-5' : 'translate-x-1'
                                            }`}
                                        />
                                    </button>
                                </div>
                                <p className="mt-2 text-sm text-gray-700">
                                    {useBrandedTemplate ? 'MyBrickLog-branded template' : 'Plaintext / no template wrapper'}
                                </p>
                            </div>

                            {/* Subject */}
                            <div>
                                <label htmlFor="email-subject" className="block text-sm font-medium text-gray-700 mb-1">
                                    Subject <span className="text-red-500">*</span>
                                </label>
                                <input
                                    id="email-subject"
                                    type="text"
                                    value={subject}
                                    onChange={e => setSubject(e.target.value)}
                                    placeholder="e.g. Important: Updated Privacy Policy"
                                    className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                                />
                            </div>

                            {/* Body */}
                            <div>
                                <div className="flex items-center justify-between mb-1">
                                    <label htmlFor="email-body" className="block text-sm font-medium text-gray-700">
                                        Body <span className="text-red-500">*</span>
                                        <span className="ml-2 text-xs text-gray-400 font-normal">(HTML supported)</span>
                                    </label>
                                    {bodyHtml.trim() && (
                                        <button
                                            type="button"
                                            onClick={() => setShowPreview(p => !p)}
                                            className="text-xs text-red-600 hover:text-red-700 font-medium"
                                        >
                                            {showPreview ? 'Hide preview' : 'Show preview'}
                                        </button>
                                    )}
                                </div>
                                <textarea
                                    id="email-body"
                                    value={bodyHtml}
                                    onChange={e => setBodyHtml(e.target.value)}
                                    rows={12}
                                    placeholder={`<p>Hi,</p>\n\n<p>We wanted to let you know that our <a href="https://mybricklog.com/privacy-policy">Privacy Policy</a> has been updated.</p>\n\n<p>Thanks,<br>The MyBrickLog Team</p>`}
                                    className="block w-full px-3 py-2 text-sm font-mono border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500 resize-y"
                                />
                                <p className="mt-1 text-xs text-gray-400">
                                    Write the main content of the email. Standard HTML tags are supported.
                                    {useBrandedTemplate
                                        ? ' The email will be wrapped in a MyBrickLog-branded template.'
                                        : ' The email will be sent without the branded template wrapper.'}
                                </p>
                            </div>

                            {/* Live preview */}
                            {showPreview && bodyHtml.trim() && (
                                <div>
                                    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Email Preview</p>
                                    <div className="border border-gray-200 rounded-lg overflow-hidden">
                                        {useBrandedTemplate ? (
                                            <>
                                                {/* Mock header */}
                                                <div className="bg-red-600 px-6 py-4">
                                                    <span className="text-white font-bold text-lg">MyBrickLog</span>
                                                </div>
                                                {/* Body preview */}
                                                <div
                                                    className="px-6 py-5 text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none"
                                                    dangerouslySetInnerHTML={{ __html: bodyHtml }}
                                                />
                                                {/* Mock footer */}
                                                <div className="bg-gray-50 border-t border-gray-200 px-6 py-3 text-xs text-gray-400 text-center">
                                                    mybricklog.com &mdash; You are receiving this email because you have an account or newsletter subscription at MyBrickLog.
                                                </div>
                                            </>
                                        ) : (
                                            <div
                                                className="px-6 py-5 text-sm text-gray-700 leading-relaxed prose prose-sm max-w-none"
                                                dangerouslySetInnerHTML={{ __html: bodyHtml }}
                                            />
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* ── Send button ─────────────────────────────────────────── */}
                    <div className="flex items-center justify-between">
                        <p className="text-sm text-gray-500">
                            {canSend ? (
                                <>
                                    Ready to send to{' '}
                                    <span className="font-medium text-gray-700">
                                        {recipientCount.toLocaleString()} recipient{recipientCount !== 1 ? 's' : ''}
                                    </span>
                                </>
                            ) : (
                                'Fill in a subject and body to send.'
                            )}
                        </p>
                        <button
                            type="button"
                            onClick={() => { setSendError(null); setShowConfirm(true); }}
                            disabled={!canSend}
                            className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                        >
                            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                            </svg>
                            Send Email
                        </button>
                    </div>
                </div>
            </div>

            {/* ── Confirm modal ───────────────────────────────────────────────── */}
            {showConfirm && (
                <ConfirmModal
                    subject={subject}
                    recipientGroup={recipientGroup}
                    recipientCount={recipientCount}
                    senderEmail={senderEmail}
                    useBrandedTemplate={useBrandedTemplate}
                    onConfirm={handleSend}
                    onCancel={() => setShowConfirm(false)}
                    isSending={isSending}
                />
            )}
        </div>
    );
};

export default AdminEmail;
